import { alpha, Box, Stack, useTheme } from '@mui/material'
import React from 'react'
import {
  toast as RHToast,
  resolveValue,
  ToastBar,
  Toaster,
} from 'react-hot-toast'

import {
  AlertMessage,
  AlertTitle,
  ToastCloseIcon,
  ToastIconWrapper,
} from './styles'

import Loading from 'components/global/Loading'

const ToastContent = ({ message, title, contained = false }) => {
  return (
    <Stack>
      {title && <AlertTitle>{title}</AlertTitle>}
      {message && <AlertMessage contained={+contained}>{message}</AlertMessage>}
    </Stack>
  )
}

const Toast = ({ position = 'bottom-right', ...props }) => {
  const theme = useTheme()
  return (
    <Toaster
      toastOptions={{
        position,
      }}
      {...props}
    >
      {(t) => {
        const { contained, message, icon } = t

        const closeIcon = (
          <ToastCloseIcon
            size={2}
            style={{ fontSize: 16 }}
            onClick={() => toast.dismiss(t.id)}
          />
        )

        return (
          <ToastBar
            toast={t}
            style={{
              boxShadow: contained
                ? `0 16px 24px -12px ${alpha(
                  theme.palette[contained].main,
                  0.64
                )}`
                : `0 12px 24px -4px ${alpha(theme.palette.common.black, 0.24)}`,
              background: contained && theme.palette[contained].main,
              borderRadius: '6px',
              width: 480,
            }}
          >
            {() => (
              <Box
                sx={{
                  display: 'flex',
                  gap: '8px',
                  padding: ' 8px 16px',
                }}
              >
                {icon}
                {resolveValue(message, t)}
                {!contained && closeIcon}
              </Box>
            )}
          </ToastBar>
        )
      }}
    </Toaster>
  )
}

let toast = {}

toast.success = (message, title, options = {}) =>
  RHToast(<ToastContent message={message} title={title} />, {
    ...options,
    icon: (
      <ToastIconWrapper color="success">
        <i className={'fa-regular fa-circle-check'} />
      </ToastIconWrapper>
    ),
  })

toast.error = (message, title, options = {}) =>
  RHToast(<ToastContent message={message} title={title} />, {
    ...options,
    icon: (
      <ToastIconWrapper color="error">
        <i className={'fa-regular fa-circle-xmark'} />
      </ToastIconWrapper>
    ),
  })

toast.info = (message, title, ...options) =>
  RHToast(<ToastContent message={message} title={title} />, {
    ...options,
    icon: (
      <ToastIconWrapper color="info">
        <i className={'fa-regular fa-circle-info'} />
      </ToastIconWrapper>
    ),
  })

toast.warning = (message, title, ...options) =>
  RHToast(<ToastContent message={message} title={title} />, {
    ...options,
    icon: (
      <ToastIconWrapper color="warning">
        <i className={'fa-regular fa-circle-exclamation'} />
      </ToastIconWrapper>
    ),
  })

toast.coloredWarning = (message, ...options) =>
  RHToast(<ToastContent message={message} contained={true} />, {
    icon: (
      <ToastIconWrapper>
        <i className={'fa-regular fa-circle-exclamation'} />
      </ToastIconWrapper>
    ),
    ...options,
    contained: 'warning',
  })

toast.coloredError = (message, ...options) =>
  RHToast(<ToastContent message={message} contained={true} />, {
    ...options,
    icon: (
      <ToastIconWrapper>
        <i className={'fa-regular fa-circle-xmark'} />
      </ToastIconWrapper>
    ),
    contained: 'error',
  })

toast.coloredInfo = (message, ...options) =>
  RHToast(<ToastContent message={message} contained={true} />, {
    ...options,
    icon: (
      <ToastIconWrapper>
        <i className={'fa-regular fa-circle-info'} />
      </ToastIconWrapper>
    ),
    contained: 'primary',
  })

toast.coloredSuccess = (message, ...options) =>
  RHToast(<ToastContent message={message} contained={true} />, {
    ...options,
    icon: (
      <ToastIconWrapper>
        <i className={'fa-regular fa-circle-check'} />
      </ToastIconWrapper>
    ),
    contained: 'success',
  })

toast.loading = (message, ...options) =>
  RHToast(<ToastContent message={message} />, {
    ...options,
    duration: Infinity,
    icon: <Loading />,
  })

export { toast }

export default Toast
